import { z } from 'zod';
import { SCENARIO_PRIORITY } from './scenarios';

export { LinkedRequirementSchema, type LinkedRequirement } from './scenarios';

export const REQUIREMENT_STATUS = ['draft', 'active', 'obsolete'] as const;
export type RequirementStatus = (typeof REQUIREMENT_STATUS)[number];

export const QualityRequirementSchema = z.object({
  id: z.string().uuid(),
  key: z.string().min(1).max(80),
  title: z.string().max(200),
  description: z.string().max(2_000),
  status: z.enum(REQUIREMENT_STATUS),
  priority: z.enum(SCENARIO_PRIORITY),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type QualityRequirement = z.infer<typeof QualityRequirementSchema>;

export const RequirementListResponseSchema = z.object({
  projectId: z.string().uuid(),
  requirements: z.array(QualityRequirementSchema),
});
export type RequirementListResponse = z.infer<typeof RequirementListResponseSchema>;

export const RequirementResponseSchema = z.object({
  requirement: QualityRequirementSchema,
});
export type RequirementResponse = z.infer<typeof RequirementResponseSchema>;

export const CreateRequirementRequestSchema = z.object({
  key: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(200),
  description: z.string().max(2_000).optional(),
  status: z.enum(REQUIREMENT_STATUS).optional(),
  priority: z.enum(SCENARIO_PRIORITY).optional(),
});
export type CreateRequirementRequest = z.infer<typeof CreateRequirementRequestSchema>;

export const PatchRequirementRequestSchema = z.object({
  key: z.string().trim().min(1).max(80).optional(),
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(2_000).optional(),
  status: z.enum(REQUIREMENT_STATUS).optional(),
  priority: z.enum(SCENARIO_PRIORITY).optional(),
});
export type PatchRequirementRequest = z.infer<typeof PatchRequirementRequestSchema>;

export const RequirementLinkedScenarioSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  title: z.string(),
  priority: z.enum(SCENARIO_PRIORITY),
  status: z.string(),
  lastExecutionStatus: z.enum(['in_progress', 'passed', 'failed', 'blocked', 'skipped']).nullable(),
});
export type RequirementLinkedScenario = z.infer<typeof RequirementLinkedScenarioSchema>;

export const RequirementDetailResponseSchema = z.object({
  requirement: QualityRequirementSchema,
  linkedScenarios: z.array(RequirementLinkedScenarioSchema),
});
export type RequirementDetailResponse = z.infer<typeof RequirementDetailResponseSchema>;

/**
 * QP-4 — Scenario-level business coverage states.
 * Linked ≠ Automated ≠ Executed ≠ Passed ≠ Currently Proven.
 */
export const BUSINESS_COVERAGE_SCENARIO_STATES = [
  'NO_AUTOMATION',
  'AUTOMATED_NOT_EXECUTED',
  'EXECUTED_BLOCKED',
  'EXECUTED_FAILED',
  'EXECUTED_PASSED',
  'PROOF_UNVERIFIED',
  'PROOF_VERIFIED',
  'PROOF_UNAVAILABLE',
  'VERSION_STALE',
] as const;
export type BusinessCoverageScenarioState = (typeof BUSINESS_COVERAGE_SCENARIO_STATES)[number];
export const BusinessCoverageScenarioStateSchema = z.enum(BUSINESS_COVERAGE_SCENARIO_STATES);

export const BusinessCoverageScenarioEntrySchema = z.object({
  requirementId: z.string().uuid(),
  scenarioId: z.string().uuid(),
  scenarioTitle: z.string(),
  scenarioStatus: z.string(),
  currentScenarioVersion: z.number().int().positive(),
  state: BusinessCoverageScenarioStateSchema,
  flowId: z.string().uuid().nullable(),
  /** Distinct linked flows — coverage is per scenario, not multiplied by flow count. */
  linkedFlowCount: z.number().int().nonnegative(),
  executionId: z.string().uuid().nullable(),
  executionKind: z.enum(['manual', 'automation']).nullable(),
  executionStatus: z.string().nullable(),
  executedScenarioVersion: z.number().int().positive().nullable(),
  runId: z.string().uuid().nullable(),
  /** True when Proof can be composed via existing GET .../executions/:id/proof */
  proofAvailable: z.boolean(),
  evidenceIntegrityStatus: z.enum(['verified', 'unverified']).nullable(),
  versionStale: z.boolean(),
});
export type BusinessCoverageScenarioEntry = z.infer<typeof BusinessCoverageScenarioEntrySchema>;

export const BusinessCoverageStateCountsSchema = z.object({
  NO_AUTOMATION: z.number().int().nonnegative(),
  AUTOMATED_NOT_EXECUTED: z.number().int().nonnegative(),
  EXECUTED_BLOCKED: z.number().int().nonnegative(),
  EXECUTED_FAILED: z.number().int().nonnegative(),
  EXECUTED_PASSED: z.number().int().nonnegative(),
  PROOF_UNVERIFIED: z.number().int().nonnegative(),
  PROOF_VERIFIED: z.number().int().nonnegative(),
  PROOF_UNAVAILABLE: z.number().int().nonnegative(),
  VERSION_STALE: z.number().int().nonnegative(),
});
export type BusinessCoverageStateCounts = z.infer<typeof BusinessCoverageStateCountsSchema>;

export const RequirementBusinessCoverageRollupSchema = z.object({
  requirementId: z.string().uuid(),
  requirementKey: z.string(),
  requirementTitle: z.string(),
  totalActiveScenarios: z.number().int().nonnegative(),
  stateCounts: BusinessCoverageStateCountsSchema,
  /**
   * Share of active linked scenarios in PROOF_VERIFIED only.
   * Explicitly NOT generic "coverage" / NOT scenario-link percentage.
   */
  proofVerifiedScenarioCoveragePercentage: z.number().min(0).max(100),
  scenarios: z.array(BusinessCoverageScenarioEntrySchema),
});
export type RequirementBusinessCoverageRollup = z.infer<typeof RequirementBusinessCoverageRollupSchema>;

export const RequirementBusinessCoverageProjectSchema = z.object({
  kind: z.literal('requirement-scenario-states'),
  /**
   * Latest relevant execution = latest automation execution for the scenario
   * with status in passed|failed|blocked|cancelled|skipped, ordered by
   * completed_at DESC NULLS LAST, updated_at DESC, created_at DESC.
   * queued/in_progress do not count as an outcome.
   */
  latestExecutionRule: z.literal('latest_automation_terminal_outcome_by_completed_at'),
  activeScenarioStatusesExcluded: z.tuple([z.literal('archived'), z.literal('obsolete')]),
  projectRollup: z.object({
    totalActiveScenarios: z.number().int().nonnegative(),
    stateCounts: BusinessCoverageStateCountsSchema,
    proofVerifiedScenarioCoveragePercentage: z.number().min(0).max(100),
  }),
  requirements: z.array(RequirementBusinessCoverageRollupSchema),
});
export type RequirementBusinessCoverageProject = z.infer<typeof RequirementBusinessCoverageProjectSchema>;

/**
 * Coverage Map (Overview slice) — compose-on-read dual-lane modes.
 * Meaningful manual = terminal manual execution (not coverage.manual authoring).
 * Automation mapped = linked flow(s). Proven = QP-4 PROOF_VERIFIED only.
 * NO_AUTOMATION ≠ MANUAL_ONLY.
 */
export const COVERAGE_MAP_MODES = ['BOTH', 'MANUAL_ONLY', 'AUTOMATION_ONLY', 'NO_COVERAGE'] as const;
export type CoverageMapMode = (typeof COVERAGE_MAP_MODES)[number];
export const CoverageMapModeSchema = z.enum(COVERAGE_MAP_MODES);

export const CoverageMapModeCountsSchema = z.object({
  BOTH: z.number().int().nonnegative(),
  MANUAL_ONLY: z.number().int().nonnegative(),
  AUTOMATION_ONLY: z.number().int().nonnegative(),
  NO_COVERAGE: z.number().int().nonnegative(),
});
export type CoverageMapModeCounts = z.infer<typeof CoverageMapModeCountsSchema>;

export const CoverageMapScenarioRowSchema = z.object({
  requirementId: z.string().uuid(),
  scenarioId: z.string().uuid(),
  scenarioTitle: z.string(),
  /** Authoring presence (steps / coverage_manual) — NOT meaningful manual coverage. */
  manualAuthored: z.boolean(),
  /** Terminal manual execution exists for this scenario. */
  manualExecuted: z.boolean(),
  manualExecutionId: z.string().uuid().nullable(),
  manualExecutionStatus: z.string().nullable(),
  automationMapped: z.boolean(),
  /** Authoritative QP-4 scenario state — never recomputed. */
  qp4State: BusinessCoverageScenarioStateSchema,
  automationExecutionId: z.string().uuid().nullable(),
  proofAvailable: z.boolean(),
  /** True only when qp4State === PROOF_VERIFIED. */
  proven: z.boolean(),
  versionStale: z.boolean(),
  /** False when VERSION_STALE; otherwise true when automation has a terminal outcome. */
  current: z.boolean(),
});
export type CoverageMapScenarioRow = z.infer<typeof CoverageMapScenarioRowSchema>;

/**
 * Per-requirement scenario counts for the Business Coverage table.
 *
 * Honesty locks:
 * - `manualExecuted` and `automationMapped` OVERLAP (a scenario can be both),
 *   so they are NOT a partition of `scenarios` and must never be presented as
 *   two halves of a whole.
 * - `passed` counts every scenario whose latest outcome was `passed`,
 *   regardless of proof standing — EXECUTED_PASSED, PROOF_VERIFIED,
 *   PROOF_UNVERIFIED, PROOF_UNAVAILABLE and VERSION_STALE all derive from a
 *   passed run. Reading EXECUTED_PASSED alone would exclude Proven scenarios.
 * - `passed` is still never `proven`. Only PROOF_VERIFIED is Proven.
 */
export const CoverageMapRequirementCountsSchema = z.object({
  /** Active linked scenarios. The denominator for every share below. */
  scenarios: z.number().int().nonnegative(),
  /** Terminal manual execution exists (meaningful-manual rule). Overlaps `automationMapped`. */
  manualExecuted: z.number().int().nonnegative(),
  /** linked_flow_count > 0. Overlaps `manualExecuted`. */
  automationMapped: z.number().int().nonnegative(),
  /** Terminal outcome in either lane. */
  executed: z.number().int().nonnegative(),
  /** Latest outcome was passed, at any proof standing. Passed is never Proven. */
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
  /** QP-4 PROOF_VERIFIED only. */
  proven: z.number().int().nonnegative(),
});
export type CoverageMapRequirementCounts = z.infer<typeof CoverageMapRequirementCountsSchema>;

export const CoverageMapRequirementRowSchema = z.object({
  requirementId: z.string().uuid(),
  requirementKey: z.string(),
  requirementTitle: z.string(),
  mode: CoverageMapModeSchema,
  /** Scenario counts behind this requirement. Compose-on-read; no new SoT. */
  counts: CoverageMapRequirementCountsSchema,
  meaningfulManual: z.boolean(),
  automationMapped: z.boolean(),
  /** ≥1 linked scenario with QP-4 automation terminal outcome (not NO_AUTOMATION / AUTOMATED_NOT_EXECUTED). */
  automationExecuted: z.boolean(),
  /** ≥1 scenario in PROOF_VERIFIED. */
  proven: z.boolean(),
  /** ≥1 scenario in VERSION_STALE. */
  stale: z.boolean(),
  /**
   * Primary business-flow assignment = earliest-created linked active scenario's business_flow_id.
   * Null when unassigned / no scenarios.
   */
  businessFlowId: z.string().uuid().nullable(),
  businessFlowName: z.string().nullable(),
  domainId: z.string().uuid().nullable(),
  domainName: z.string().nullable(),
  moduleId: z.string().uuid().nullable(),
  moduleName: z.string().nullable(),
  scenarios: z.array(CoverageMapScenarioRowSchema),
});
export type CoverageMapRequirementRow = z.infer<typeof CoverageMapRequirementRowSchema>;

export const CoverageMapFeatureRollupSchema = z.object({
  businessFlowId: z.string().uuid().nullable(),
  businessFlowName: z.string(),
  domainId: z.string().uuid().nullable(),
  domainName: z.string().nullable(),
  moduleId: z.string().uuid().nullable(),
  moduleName: z.string().nullable(),
  /** Unique requirements assigned to this feature (primary-flow rule). */
  requirementCount: z.number().int().nonnegative(),
  modeCounts: CoverageMapModeCountsSchema,
  provenRequirementCount: z.number().int().nonnegative(),
  /** Sum of this feature's requirement counts. Same honesty locks apply. */
  counts: CoverageMapRequirementCountsSchema,
  requirements: z.array(CoverageMapRequirementRowSchema),
});
export type CoverageMapFeatureRollup = z.infer<typeof CoverageMapFeatureRollupSchema>;

export const CoverageMapApplicationRollupSchema = z.object({
  /** Denominator = unique requirements in the project. */
  denominator: z.literal('unique_requirements'),
  totalRequirements: z.number().int().nonnegative(),
  modeCounts: CoverageMapModeCountsSchema,
  /** Requirements with ≥1 automation-mapped scenario. */
  automatedRequirementCount: z.number().int().nonnegative(),
  /** Requirements with ≥1 automation-executed scenario (QP-4 terminal). */
  executedRequirementCount: z.number().int().nonnegative(),
  /** Requirements with ≥1 PROOF_VERIFIED scenario. */
  provenRequirementCount: z.number().int().nonnegative(),
  /** Requirements with ≥1 VERSION_STALE scenario. */
  staleRequirementCount: z.number().int().nonnegative(),
});
export type CoverageMapApplicationRollup = z.infer<typeof CoverageMapApplicationRollupSchema>;

export const CoverageMapProjectSchema = z.object({
  kind: z.literal('requirement-coverage-map'),
  /**
   * Meaningful manual = terminal manual execution on a linked active scenario.
   * coverage.manual / steps alone never imply MANUAL_ONLY or BOTH.
   */
  meaningfulManualRule: z.literal('terminal_manual_execution'),
  automationMappedRule: z.literal('linked_flow_count_gt_0'),
  provenRule: z.literal('qp4_PROOF_VERIFIED'),
  /**
   * Passed counts a passed latest outcome at ANY proof standing, so earning
   * Proof never removes a scenario from Passed. Passed still ≠ Proven.
   */
  passedRule: z.literal('latest_outcome_passed_any_proof_standing'),
  featureAssignmentRule: z.literal('primary_business_flow_earliest_linked_scenario'),
  application: CoverageMapApplicationRollupSchema,
  features: z.array(CoverageMapFeatureRollupSchema),
  requirements: z.array(CoverageMapRequirementRowSchema),
});
export type CoverageMapProject = z.infer<typeof CoverageMapProjectSchema>;

export const RequirementCoverageResponseSchema = z.object({
  projectId: z.string().uuid(),
  /**
   * LEGACY — count of requirements with ≥1 linked scenario.
   * NOT Proof-backed business coverage. See `scenarioLinkSemantics`
   * and `businessCoverage` (QP-4).
   */
  totalRequirements: z.number().int().nonnegative(),
  coveredRequirements: z.number().int().nonnegative(),
  partiallyCoveredRequirements: z.number().int().nonnegative(),
  uncoveredRequirements: z.number().int().nonnegative(),
  coveragePercentage: z.number().min(0).max(100),
  /** Honesty lock: legacy percentage is scenario-link presence only. */
  scenarioLinkSemantics: z.literal('requirement_has_linked_scenario'),
  /** QP-4 — compose-on-read Requirement → Scenario coverage states. */
  businessCoverage: RequirementBusinessCoverageProjectSchema,
  /** Overview Coverage Map — dual-lane modes; compose-on-read; no new SoT. */
  coverageMap: CoverageMapProjectSchema,
});
export type RequirementCoverageResponse = z.infer<typeof RequirementCoverageResponseSchema>;
